import {
  AccountDetailsContent,
  AccountMainDetails,
  UserDocumentationsFileTypes,
} from "@bounce/shared/consumer/pages/account";
import {BalanceBreakdown} from "@bounce/shared/consumer/pages/account-dashboard";
import {FileRead} from "@bounce/shared/declarations";
import NiceModal, {NiceModalHocProps, useModal} from "@ebay/nice-modal-react";
import {Pages} from "~/bi-events/enum";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {generatePath, useNavigate} from "react-router-dom";
import {downloadUserFiles, useGetUserFilesQuery, useGetAccount} from "~/api/requests";
import {useAmplitude} from "~/contexts";
import {useAuth} from "@bounce/shared/contexts";
import {urls} from "~/routes/consumerUrls";

interface IAccountDetailsDialog extends NiceModalHocProps {
  clearCampaignFromState?: () => void;
  accountId?: string;
  isPreChargeOff?: boolean;
  isBundle?: boolean;
  onResolve?: (e: React.MouseEvent) => void;
  hideActions?: boolean;
}

const AccountDetailsDialog = NiceModal.create(
  ({
    clearCampaignFromState,
    accountId,
    isPreChargeOff = false,
    isBundle = false,
    onResolve,
    hideActions = false,
  }: IAccountDetailsDialog) => {
    const {pageViewEvent, pageClickEvent} = useAmplitude();
    const navigate = useNavigate();
    const {loggedIn} = useAuth();
    const {data: account} = useGetAccount({accountId}, {enabled: !!accountId});
    const [breakdownView, setBreakdownView] = useState<boolean>(false);
    const modal = useModal();
    const isInCampaign = !!clearCampaignFromState;

    const getUserFiles = useGetUserFilesQuery(
      {accountId, includeFiles: UserDocumentationsFileTypes, tab: "dashboard"},
      {enabled: loggedIn && !!accountId}
    );

    const userFiles = useMemo<FileRead[] | undefined>(() => getUserFiles.data, [getUserFiles.data]);

    const cancelHandler = useCallback((): void => {
      pageClickEvent({page: Pages.ACCOUNT_DETAILS, cta: "close"});
    }, []);

    const handleFileDispute = () => {
      pageClickEvent({page: Pages.ACCOUNT_DETAILS, cta: "file_dispute", data: {accountId: accountId}});
      modal.resolve(false);
      modal.hide();
      navigate(generatePath(urls.dispute.url, {accountId}));
    };

    const handleVerificationRequest = () => {
      pageClickEvent({page: Pages.ACCOUNT_DETAILS, cta: "request_verification", data: {accountId: accountId}});
      modal.resolve(false);
      modal.hide();
      navigate(generatePath(urls.verificationRequest.url, {accountId}));
    };

    useEffect(() => {
      if (isInCampaign) {
        clearCampaignFromState();
      }
      pageViewEvent({page: Pages.ACCOUNT_DETAILS, data: {accountId: accountId}});
    }, []);

    const showDisputeLink = useMemo(() => {
      if (account) {
        return (
          !account.active_payment_plan &&
          account.status !== "creditor_recalled" &&
          account.status !== "closed" &&
          !account?.active_dispute
        );
      }
    }, [account]);

    return (
      <AccountDetailsContent
        title={isPreChargeOff || isBundle ? "Balance details" : "Account details"}
        cancelHandler={cancelHandler}
        account={account}
        isLoading={getUserFiles.isInitialLoading}
        userFiles={userFiles}
        isInCampaign={isInCampaign}
        breakdownView={breakdownView}
        setBreakdownView={setBreakdownView}
        handleFileDispute={handleFileDispute}
        handleVerificationRequest={handleVerificationRequest}
        downloadUserFiles={downloadUserFiles}
        AccountMainDetails={<AccountMainDetails account={account} size={16} />}
        BalanceBreakdown={<BalanceBreakdown account={account} />}
        showDisputeLink={!hideActions && showDisputeLink}
        onResolve={!hideActions && !account?.active_payment_plan ? onResolve : undefined}
      />
    );
  }
);
export default AccountDetailsDialog;
